import { useCallback, useEffect, useRef, useState } from 'react';

const useCrossWindowState = <T extends unknown>(
  stateKey: string,
  defaultValue: T,
): [state: T, setState: (state: T) => void, removeKey: () => void] => {
  const [state, setState] = useState<T>(defaultValue);
  const isNewSession = useRef(true);

  useEffect(() => {
    if (isNewSession.current) {
      const currentState = localStorage.getItem(stateKey);
      if (currentState) {
        setState(JSON.parse(currentState));
      } else {
        setState(defaultValue);
      }
      isNewSession.current = false;
      return;
    }
    try {
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch {
      return;
    }
  }, [state, stateKey, defaultValue]);

  useEffect(() => {
    const onReceieveMessage = (e: StorageEvent) => {
      const { key, newValue } = e;
      if (key === stateKey && newValue) {
        setState(JSON.parse(newValue ?? ''));
      }
    };
    window.addEventListener('storage', onReceieveMessage);
    return () => window.removeEventListener('storage', onReceieveMessage);
  }, [stateKey, setState]);

  const removeKey = useCallback(() => {
    return localStorage.removeItem(stateKey);
  }, [stateKey]);

  return [state, setState, removeKey];
};

export default useCrossWindowState;
